import {
  Badge,
  confirm,
  createExpandableColumn,
  createSortableColumn,
  DataTable,
  type TanstackReactTable,
} from "@devhop/ui";
import { formatDateLocal } from "@/shared/lib/date-time";
import { EditIcon, TrashIcon } from "lucide-react";
import { RowActions } from "@/shared/ui/RowActions";
import type { KPIAssignmentListItem } from "../../domain/types";

type KPIAssignmentTableProps = {
  canManage: boolean;
  isLoading: boolean;
  data: KPIAssignmentListItem[];
  offset: number;
  limit: number;
  totalCount: number;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  onPaginationChange: (offset: number, limit: number) => void;
  onSortingChange: (id: string, desc: boolean) => void;
  onEdit: (assignment: KPIAssignmentListItem) => void;
  onDelete: (id: string) => Promise<void>;
};

export function KPIAssignmentTable({
  canManage,
  isLoading,
  data,
  offset,
  limit,
  totalCount,
  sortBy,
  sortOrder,
  onPaginationChange,
  onSortingChange,
  onEdit,
  onDelete,
}: KPIAssignmentTableProps) {
  const getAssignmentTypeBadge = (type: string) => {
    const config = {
      employee: { label: "ພະນັກງານ", variant: "default" as const },
      position: { label: "ຕໍາແໜ່ງ", variant: "secondary" as const },
      department: { label: "ພະແນກ", variant: "outline" as const },
    };
    const cfg = config[type as keyof typeof config] || config.employee;
    return (
      <Badge variant={cfg.variant} className="text-xs">
        {cfg.label}
      </Badge>
    );
  };

  const getAssignedToName = (assignment: KPIAssignmentListItem) => {
    if (assignment.assignmentType === "employee") {
      return assignment.employeeName || "-";
    }
    if (assignment.assignmentType === "position") {
      return assignment.positionName || "-";
    }
    if (assignment.assignmentType === "department") {
      return assignment.departmentName || "-";
    }
    return "-";
  };

  const columns: TanstackReactTable.ColumnDef<KPIAssignmentListItem>[] = [
    createExpandableColumn<KPIAssignmentListItem>(""),
    createSortableColumn<KPIAssignmentListItem>("kpiName", "KPI", {
      size: 200,
    }),
    {
      id: "assignmentType",
      header: "ປະເພດ",
      cell: ({ row }) => getAssignmentTypeBadge(row.original.assignmentType),
      size: 120,
    },
    {
      id: "assignedTo",
      header: "ຜູ້ຮັບຜິດຊອບ",
      cell: ({ row }) => (
        <div className="max-w-[200px] truncate text-sm">
          {getAssignedToName(row.original)}
        </div>
      ),
      size: 200,
    },
    {
      id: "startDate",
      header: "ວັນທີເລີ່ມຕົ້ນ",
      cell: ({ row }) => {
        const date = row.original.startDate;
        if (!date) return "-";
        try {
          return formatDateLocal(date);
        } catch {
          return date;
        }
      },
      size: 120,
    },
    {
      id: "endDate",
      header: "ວັນທີສິ້ນສຸດ",
      cell: ({ row }) => {
        const date = row.original.endDate;
        if (!date) return "-";
        try {
          return formatDateLocal(date);
        } catch {
          return date;
        }
      },
      size: 120,
    },
    {
      id: "isActive",
      header: "ສະຖານະ",
      cell: ({ row }) => (
        <Badge
          variant={row.original.isActive ? "default" : "outline"}
          className="text-xs"
        >
          {row.original.isActive ? "ກຳລັງໃຊ້ງານ" : "ປິດການໃຊ້ງານ"}
        </Badge>
      ),
      size: 120,
    },
    {
      id: "actions",
      cell: ({ row }) => {
        const assignment = row.original;
        const id = assignment.id;
        if (!canManage) return null;

        const actions = [
          {
            label: "ແກ້ໄຂ",
            icon: <EditIcon className="h-4 w-4" />,
            onClick: () => onEdit(assignment),
          },
          {
            label: "ລຶບ",
            variant: "destructive" as const,
            icon: <TrashIcon className="h-4 w-4" />,
            onClick: async () => {
              const ok = await confirm({
                title: "ລຶບການມອບໝາຍ",
                description: "ທ່ານແນ່ໃຈບໍ່ວ່າຈະລຶບການມອບໝາຍນີ້?",
                actionText: "ລຶບ",
                ActionProps: {
                  variant: "destructive",
                },
              });
              if (ok) await onDelete(id);
            },
          },
        ];

        return <RowActions actions={actions} maxInline={2} />;
      },
      size: 100,
    },
  ];

  return (
    <div data-tourid="assignment-table">
      <DataTable<KPIAssignmentListItem, unknown>
        noDataMessage="ບໍ່ພົບການມອບໝາຍ KPI"
        isLoading={isLoading}
        columns={columns}
        data={data}
        offset={offset}
        limit={limit}
        totalCount={totalCount}
        onPaginationChange={(pagination) =>
          onPaginationChange(pagination.offset, pagination.limit)
        }
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSortingChange={(sorting) => {
          if (sorting[0]?.id === "") return;
          onSortingChange(sorting[0]?.id as string, !!sorting[0]?.desc);
        }}
        renderExpandedContent={({ original }) => {
          return (
            <div className="space-y-4 rounded-lg bg-muted/50 p-4">
              <div>
                <h4 className="mb-2 font-medium text-sm">ຊື່ KPI</h4>
                <p className="text-muted-foreground text-sm">
                  {original.kpiName}
                </p>
              </div>
              <div>
                <h4 className="mb-2 font-medium text-sm">ປະເພດການມອບໝາຍ</h4>
                <div>{getAssignmentTypeBadge(original.assignmentType)}</div>
              </div>
              <div>
                <h4 className="mb-2 font-medium text-sm">ຜູ້ຮັບຜິດຊອບ</h4>
                <p className="text-muted-foreground text-sm">
                  {getAssignedToName(original)}
                </p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h4 className="mb-2 font-medium text-sm">ວັນທີເລີ່ມຕົ້ນ</h4>
                  <p className="text-muted-foreground text-sm">
                    {original.startDate
                      ? formatDateLocal(original.startDate)
                      : "-"}
                  </p>
                </div>
                <div>
                  <h4 className="mb-2 font-medium text-sm">ວັນທີສິ້ນສຸດ</h4>
                  <p className="text-muted-foreground text-sm">
                    {original.endDate
                      ? formatDateLocal(original.endDate)
                      : "ບໍ່ກຳນົດ"}
                  </p>
                </div>
              </div>
            </div>
          );
        }}
      />
    </div>
  );
}
